import type { BibleBook } from "./types";

/** Hebrews to Revelation — the general letters and the apocalypse. */
export const GENERAL: BibleBook[] = [
  {
    slug: "hebrews",
    name: "Hebrews",
    testament: "new",
    genre: "letter",
    order: 58,
    chapters: 13,
    author: "Unknown; the letter never says, and no early consensus formed",
    written: "Before 70 AD on most reckonings",
    audience: "Jewish Christians under pressure to go back",
    argument:
      "A sustained argument that what they already had was not being taken away but completed, written to people considering giving up.",
    summary: [
      "Hebrews is not really a letter; it calls itself a word of exhortation and reads like a sermon. Its author is unknown, which was already true in the second century — proposals have included Paul, Barnabas, Apollos and Priscilla, and none has ever settled.",
      "The argument runs through a series of comparisons: better than angels, than Moses, than the priesthood, than the sacrifices. The audience appears to be Jewish Christians under enough pressure to consider returning to a safer, legally recognised practice.",
      "Chapter 11 is the famous one, a roll call of people who acted on things they could not see, and it is careful to note that many of them did not receive what was promised.",
    ],
    startHere:
      "Chapter 11, then chapter 12 verses 1 to 3, which is where the roll call is applied to the reader.",
    themes: ["better", "priesthood", "faith", "perseverance"],
    keyChapters: [
      {
        chapter: 4,
        why: "The sabbath rest that remains, and the word described as sharper than a two-edged sword.",
      },
      {
        chapter: 11,
        why: "The roll call of people who acted on what they could not see, several of whom died without receiving it.",
      },
      {
        chapter: 12,
        why: "The cloud of witnesses, and the instruction to lay aside every weight and run.",
      },
    ],
    known: {
      text: "Now faith is assurance of things hoped for, proof of things not seen.",
      ref: "Hebrews 11:1",
    },
    misread:
      "That chapter 11 is a list of successes. It includes people who were sawn in two, wandered in deserts and died without receiving what was promised, and it says so explicitly near the end.",
    faq: [
      {
        q: "Who wrote the book of Hebrews?",
        a: "Nobody knows. The text is anonymous, no early consensus existed, and suggestions have ranged from Paul to Barnabas, Apollos and Priscilla.",
      },
      {
        q: "What is Hebrews 11 about?",
        a: "A survey of people who acted on what they could not see. It is often read as a hall of fame, but the chapter explicitly includes those who were tortured and killed and who did not receive what was promised.",
      },
    ],
  },
  {
    slug: "james",
    name: "James",
    testament: "new",
    genre: "letter",
    order: 59,
    chapters: 5,
    author: "James, most likely the brother of Jesus",
    written: "Possibly 40s to 60s AD",
    audience: "Scattered Jewish Christian congregations",
    argument:
      "The most practical book in the New Testament, closer to Proverbs than to Paul, and famously hard to reconcile with him on faith and works.",
    summary: [
      "James reads like wisdom literature: short, punchy, concrete, and organised loosely by topic rather than by argument. It is about speech, money, favouritism, patience and what people actually do.",
      "Its most discussed passage is chapter 2, where faith without works is called dead. Luther found it hard to square with Paul and called it an epistle of straw. The usual resolution is that the two are addressing different questions — Paul, how a person is accepted; James, what a living faith looks like from outside.",
      "The material on the tongue in chapter 3 is the sharpest short writing about speech in the Bible, and it long predates any medium that made speech instant and public.",
    ],
    startHere:
      "Chapter 1, which sets out most of the book's themes in twenty-seven verses.",
    themes: ["speech", "action", "favouritism", "patience"],
    keyChapters: [
      {
        chapter: 1,
        why: "Trials, wisdom for the asking, and the instruction to be doers rather than hearers.",
      },
      {
        chapter: 2,
        why: "Favouritism towards the rich, and the passage about faith without works.",
      },
      {
        chapter: 3,
        why: "The tongue — a small rudder, a spark, and the sharpest short writing about speech in the Bible.",
      },
    ],
    known: {
      text: "But be doers of the word, and not only hearers, deluding your own selves.",
      ref: "James 1:22",
    },
    misread:
      "That James contradicts Paul on faith. They use the same words for different questions — Paul is arguing about how someone is accepted, James about how a real faith is recognisable — and both quote Abraham to make their case.",
    faq: [
      {
        q: "Does James contradict Paul on faith and works?",
        a: "They address different questions with overlapping vocabulary. Paul argues that acceptance is not earned; James argues that a faith producing nothing is not the living kind. Both use Abraham as their example.",
      },
      {
        q: "Who was James?",
        a: "Most likely the brother of Jesus, who led the Jerusalem church and appears in Acts 15. The letter's Jewish framing and wisdom style fit that setting.",
      },
    ],
    topics: ["bible-verses-about-temptation"],
  },
  {
    slug: "1-peter",
    name: "1 Peter",
    testament: "new",
    genre: "letter",
    order: 60,
    chapters: 5,
    author: "Peter, with Silvanus named as scribe",
    written: "Probably 60s AD",
    audience: "Christians in Asia Minor being treated as outsiders",
    argument:
      "A letter about being an unpopular minority, written to people suffering social hostility rather than official persecution, and refusing to let them retaliate.",
    summary: [
      "1 Peter addresses churches scattered across what is now Turkey, and its recurring image is exile — resident aliens, strangers, people at home nowhere. The suffering it describes is mostly social: slander, exclusion, being thought subversive.",
      "The instruction throughout is not to answer in kind. Repay evil with blessing, be ready to explain your hope with gentleness and respect, and let conduct do the arguing.",
      "It is also the source of the shortest and bluntest verse about worry in the New Testament, sitting in a passage about humility under pressure.",
    ],
    startHere:
      "Chapter 3, from verse 8, which contains both the instruction not to retaliate and the line about giving a reason for hope.",
    themes: ["exile", "suffering", "conduct", "hope"],
    keyChapters: [
      {
        chapter: 2,
        why: "A chosen people and living stones, addressed to people treated as nobody in particular.",
      },
      {
        chapter: 3,
        why: "Do not repay evil with evil, and be ready to give a reason for your hope with gentleness.",
      },
      {
        chapter: 5,
        why: "Casting your anxiety on him, in a passage about humility under pressure.",
      },
    ],
    known: {
      text: "casting all your worries on him, because he cares for you.",
      ref: "1 Peter 5:7",
    },
    misread:
      "That its instruction to submit endorses abusive authority. The letter is strategic advice to a vulnerable minority about not giving their accusers ammunition, and it has been misused for centuries as though it were a general endorsement of whoever holds power.",
    faq: [
      {
        q: "What is 1 Peter about?",
        a: "Living as an unpopular minority — enduring social hostility without retaliating, and letting conduct rather than argument make the case.",
      },
      {
        q: "What does cast all your anxiety on him mean?",
        a: "The verb is a throwing word, and the reason given is not that the worry was trivial but that someone else is willing to carry it. It sits in a passage about humility under pressure.",
      },
    ],
    topics: ["bible-verses-for-anxiety-at-night"],
  },
  {
    slug: "2-peter",
    name: "2 Peter",
    testament: "new",
    genre: "letter",
    order: 61,
    chapters: 3,
    author: "Peter, though authorship is the most debated in the New Testament",
    written: "Uncertain; possibly late first or early second century",
    audience: "Churches facing teachers who denied the return of Christ",
    argument:
      "A short, fierce letter against teachers who had concluded that nothing was going to happen, containing the New Testament's most-quoted line about God's timing.",
    summary: [
      "2 Peter is preoccupied with false teachers, and chapter 2 attacks them at length and with vivid contempt — much of it shared, almost word for word, with Jude.",
      "The specific problem is scoffers asking where the promise of his coming is, since everything continues as it always has. The letter's answer is about time rather than evidence: a day is like a thousand years, and the delay is patience rather than absence.",
      "Its authorship is the most contested in the New Testament, on grounds of style, its relationship to Jude, and its late attestation. Early church writers were themselves uncertain about it.",
    ],
    startHere:
      "Chapter 3, which contains the argument about delay and is the part of the letter still doing work.",
    themes: ["false teachers", "knowledge", "the delay", "patience"],
    keyChapters: [
      {
        chapter: 1,
        why: "The list of qualities to add to faith, and the claim to have been an eyewitness.",
      },
      {
        chapter: 3,
        why: "The answer to the scoffers — a day is like a thousand years, and the delay is patience.",
      },
    ],
    known: {
      text: "The Lord is not slow concerning his promise, as some count slowness; but he is patient with us, not wishing that anyone should perish, but that all should come to repentance.",
      ref: "2 Peter 3:9",
    },
    misread:
      "That a thousand years as a day is a formula for calculating prophecy. It is an argument that delay is not evidence of absence, made against people who had drawn exactly that conclusion.",
    faq: [
      {
        q: "Why is 2 Peter's authorship debated?",
        a: "Its Greek style differs sharply from 1 Peter, much of chapter 2 parallels Jude, and it is attested later than any other New Testament book. Early Christian writers were themselves unsure about it.",
      },
      {
        q: "What does a day is like a thousand years mean?",
        a: "It answers people who said the promised return had not happened so it never would. The point is that delay is patience rather than absence — not a formula for calculating dates.",
      },
    ],
  },
  {
    slug: "1-john",
    name: "1 John",
    testament: "new",
    genre: "letter",
    order: 62,
    chapters: 5,
    author: "Traditionally John; formally anonymous",
    written: "Probably 90s AD",
    audience: "A community that has just split",
    argument:
      "Written after a group left the church, giving those who stayed three tests for telling the real thing from the imitation.",
    summary: [
      "1 John has no greeting, no named sender and no named recipients, which is unusual. What it does have is a situation: some people have left the community, and the letter says plainly that they went out from us.",
      "The response is a set of tests, repeated in cycles rather than argued in sequence: do you keep his commands, do you love your brother, do you believe he came in the flesh. The last one suggests the departing group denied the physical reality of the incarnation.",
      "It is also the most reassuring book in the New Testament for people troubled about their standing, and it contains the plainest sentence about confession anywhere in Scripture.",
    ],
    startHere:
      "Chapter 1, which is short and contains the confession verse, then chapter 4 on love.",
    themes: ["light", "love", "assurance", "truth"],
    keyChapters: [
      {
        chapter: 1,
        why: "Walking in the light, and the plainest sentence about confession in Scripture.",
      },
      {
        chapter: 3,
        why: "Love in action rather than in words, and the reassurance for a heart that condemns itself.",
      },
      {
        chapter: 4,
        why: "God is love, and the test about Christ come in the flesh that the letter keeps returning to.",
      },
    ],
    known: {
      text: "If we confess our sins, he is faithful and righteous to forgive us the sins and to cleanse us from all unrighteousness.",
      ref: "1 John 1:9",
    },
    misread:
      "That its tests are for examining other people. The letter's repeated purpose statement is that its readers may know they have eternal life; the tests are for reassurance, not for auditing the congregation.",
    faq: [
      {
        q: "Why was 1 John written?",
        a: "After a group left the community, apparently denying that Christ had come in the flesh. The letter gives those who remained ways of recognising the real thing.",
      },
      {
        q: "What are the tests in 1 John?",
        a: "Obedience, love for other believers, and belief that Christ came in the flesh — repeated in cycles through the letter rather than laid out in order.",
      },
    ],
    topics: ["bible-verses-about-forgiveness"],
  },
  {
    slug: "2-john",
    name: "2 John",
    testament: "new",
    genre: "letter",
    order: 63,
    chapters: 1,
    author: '"The elder"; traditionally John',
    written: "Probably 90s AD",
    audience: "A congregation, addressed as a lady and her children",
    argument:
      "Thirteen verses about hospitality, and the uncomfortable instruction not to extend it to travelling teachers spreading the thing 1 John was written against.",
    summary: [
      "2 John is one of the two shortest books in the Bible, written on what was probably a single sheet. It is addressed to the chosen lady and her children, almost certainly a congregation rather than an individual.",
      "It repeats the themes of 1 John in miniature — walking in truth, loving one another, the denial that Christ came in the flesh — and then applies them to a practical situation.",
      "That application is the difficult part: do not receive such a teacher into your house or greet him. In a world where travelling teachers depended entirely on hospitality, withholding it was the only sanction available, which is worth knowing before the verse is used to justify shunning people generally.",
    ],
    startHere: "The whole letter. Thirteen verses, two minutes.",
    themes: ["truth", "love", "hospitality", "discernment"],
    keyChapters: [
      {
        chapter: 1,
        why: "The whole letter — thirteen verses on truth, love and who to receive into the house.",
      },
    ],
    known: {
      text: "This is love, that we should walk according to his commandments.",
      ref: "2 John 1:6",
    },
    misread:
      "That it licenses refusing hospitality to anyone you disagree with. The instruction concerns travelling teachers promoting a specific denial, in a context where hospitality was the only means of support and therefore the only available sanction.",
    faq: [
      {
        q: "Who is the chosen lady in 2 John?",
        a: "Almost certainly a congregation rather than a person — the letter shifts between singular and plural in ways that fit a church addressed collectively.",
      },
      {
        q: "How long is 2 John?",
        a: "Thirteen verses, one chapter. It and 3 John are the shortest books in the Bible.",
      },
    ],
  },
  {
    slug: "3-john",
    name: "3 John",
    testament: "new",
    genre: "letter",
    order: 64,
    chapters: 1,
    author: '"The elder"; traditionally John',
    written: "Probably 90s AD",
    audience: "Gaius, one man in a church with a difficult leader",
    argument:
      "Fifteen verses about a church politics problem: a man named Diotrephes who likes being first and will not receive anyone the elder sends.",
    summary: [
      "3 John is a personal note to Gaius, commending him for supporting travelling missionaries and warning him about Diotrephes, who refuses to welcome them and puts out anyone who does.",
      "It is the most ordinary document in the New Testament, and that is its value: a first-century church with a leader guarding his position, and a letter working out what to do about it.",
      "It also contains the elder's most human line, about having no greater joy than hearing that his children are walking in truth.",
    ],
    startHere: "The whole letter. Fifteen verses.",
    themes: ["hospitality", "support", "church politics", "imitation"],
    keyChapters: [
      {
        chapter: 1,
        why: "The whole letter — Gaius commended, Diotrephes named, and Demetrius vouched for.",
      },
    ],
    known: {
      text: "I have no greater joy than this: to hear about my children walking in truth.",
      ref: "3 John 1:4",
    },
    misread:
      "That it is too minor to bother with. It is the clearest surviving snapshot of an ordinary first-century congregational dispute, which is exactly what makes it useful.",
    faq: [
      {
        q: "What is 3 John about?",
        a: "Hospitality to travelling missionaries, and a church leader named Diotrephes who refuses to receive them and excludes those who do.",
      },
      {
        q: "Who was Diotrephes?",
        a: "A figure in the congregation described as loving to be first, who would not welcome the elder's messengers. Nothing more is known about him than these few verses say.",
      },
    ],
  },
  {
    slug: "jude",
    name: "Jude",
    testament: "new",
    genre: "letter",
    order: 65,
    chapters: 1,
    author: "Jude, describing himself as the brother of James",
    written: "Probably 60s to 80s AD",
    audience: "A church that had let the wrong teachers in",
    argument:
      "Twenty-five verses of warning that quote two books outside the canon and end with one of the most beautiful sentences in the New Testament.",
    summary: [
      "Jude opens by saying he had intended to write about salvation and changed his mind because something urgent came up: teachers who had slipped in and were using grace as a licence.",
      "The letter is short, angry and vivid — wandering stars, waterless clouds, autumn trees without fruit. Much of its content parallels 2 Peter 2, and the relationship between the two is a long-running puzzle.",
      "It also quotes 1 Enoch and alludes to the Assumption of Moses, neither of which is in anyone's canon, which has made it a standing question in discussions of what quotation implies.",
    ],
    startHere: "The whole letter, and read the last two verses aloud.",
    themes: ["contending", "false teachers", "judgement", "keeping"],
    keyChapters: [
      {
        chapter: 1,
        why: "The whole letter — a warning that changed subject at the last minute, ending in a doxology.",
      },
    ],
    known: {
      text: "Now to him who is able to keep them from stumbling, and to present you faultless before the presence of his glory in great joy,",
      ref: "Jude 1:24",
    },
    misread:
      "That its quotation of 1 Enoch settles anything about that book's status. Ancient writers quoted material they found apt without thereby endorsing it as Scripture, and the letter makes no claim either way.",
    faq: [
      {
        q: "Why does Jude quote the book of Enoch?",
        a: "Because the passage suited his argument. Quotation in ancient writing did not imply canonical status, and Jude makes no claim about 1 Enoch's authority.",
      },
      {
        q: "How is Jude related to 2 Peter?",
        a: "Much of Jude parallels 2 Peter chapter 2 closely. Whether one used the other or both drew on a common source has never been settled.",
      },
    ],
  },
  {
    slug: "revelation",
    name: "Revelation",
    testament: "new",
    genre: "apocalyptic",
    order: 66,
    chapters: 22,
    author: "John, on Patmos; his identity is disputed",
    written: "Probably 90s AD",
    audience: "Seven named churches in Asia Minor",
    argument:
      "A letter to seven real churches, written in a genre with its own conventions, which is almost always read as though it were a coded newspaper about the future.",
    summary: [
      "Revelation is apocalyptic literature, a genre with established conventions that its first readers knew and modern readers usually do not. It uses vision, symbolic numbers and beasts standing for empires, and it is addressed in its opening chapters to seven specific congregations with specific problems.",
      "Interpretations divide broadly into those reading it as about the first century, about all of history, about the end, or about none of these in a literal sense. Sincere Christians hold all four, and any summary claiming the question is settled is overstating.",
      "What is not in dispute is where it ends: not with destruction but with a city, a river, a tree whose leaves are for healing, and God wiping tears off faces.",
    ],
    startHere:
      "Chapters 2 and 3, the letters to the seven churches, which anchor everything that follows in real places. Then chapter 21.",
    themes: ["worship", "empire", "witness", "new creation"],
    keyChapters: [
      {
        chapter: 2,
        why: "The letters to four of the seven churches, each addressing a specific real situation.",
      },
      {
        chapter: 5,
        why: "The scroll nobody can open, and the lion who turns out to be a slaughtered lamb.",
      },
      {
        chapter: 21,
        why: "The city coming down, and God wiping tears off faces — where the book actually ends up.",
      },
    ],
    known: {
      text: "He will wipe away every tear from their eyes. Death will be no more; neither will there be mourning, nor crying, nor pain any more. The first things have passed away.",
      ref: "Revelation 21:4",
    },
    misread:
      "That it is a timetable. It belongs to a genre that uses symbol and number the way a political cartoon uses caricature, and reading it as a coded schedule has produced a long history of confident predictions that did not happen.",
    faq: [
      {
        q: "What is the book of Revelation about?",
        a: "A vision addressed to seven churches in Asia Minor, using apocalyptic imagery to argue that the empire persecuting them is not the final power. It ends with a renewed creation rather than with destruction.",
      },
      {
        q: "How should Revelation be interpreted?",
        a: "Four broad approaches exist — first-century, historical, futurist and symbolic — and serious Christians hold each. Knowing that the book belongs to a genre with conventions matters more than choosing between them quickly.",
      },
    ],
    topics: ["bible-verses-about-grief"],
  },
];
